// src/pages/LiveQuotes.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../utils/api';

export default function LiveQuotes() {
  const [quotes,  setQuotes]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    authFetch('/api/live-quotes')
      .then(r => r.json())
      .then(d => {
        setQuotes(d.quotes || []);
        setLastUpdated(new Date());
        setLoading(false);
      })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  const fmt = (n, d = 2) => n != null ? Number(n).toLocaleString('en-IN', { minimumFractionDigits: d, maximumFractionDigits: d }) : '—';

  return (
    <div style={{ padding: '1rem', maxWidth: 1000 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
        <h1 style={{ margin: 0, fontSize: '1.35rem', fontWeight: 800, color: '#e2e8f0' }}>Live Quotes</h1>
        <button
          onClick={load}
          disabled={loading}
          style={{ padding: '0.4rem 0.9rem', background: '#0ea5e9', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer', opacity: loading ? 0.7 : 1 }}
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>
      <p style={{ margin: '0 0 1rem', fontSize: '0.78rem', color: '#64748b' }}>
        {quotes.length} symbol{quotes.length !== 1 ? 's' : ''}
        {lastUpdated && <span style={{ marginLeft: 8 }}>· Updated {lastUpdated.toLocaleTimeString()}</span>}
      </p>

      {error && (
        <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 7, padding: '0.7rem 1rem', color: '#991b1b', marginBottom: '0.75rem' }}>
          {error}
          {error.toLowerCase().includes('live_quotes') && (
            <p style={{ margin: '0.3rem 0 0', fontSize: '0.78rem' }}>
              The <code>live_quotes</code> table may not exist yet — it's populated by the live ticker service.
            </p>
          )}
        </div>
      )}

      {!loading && quotes.length === 0 && !error && (
        <p style={{ color: '#94a3b8', textAlign: 'center', padding: '2rem' }}>
          No live quotes available. The ticker service may not be running.
        </p>
      )}

      {quotes.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto', maxHeight: '75vh', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead style={{ position: 'sticky', top: 0 }}>
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                  <th style={th}>Symbol</th>
                  <th style={{ ...th, textAlign: 'right' }}>LTP</th>
                  <th style={{ ...th, textAlign: 'right' }}>Open</th>
                  <th style={{ ...th, textAlign: 'right' }}>High</th>
                  <th style={{ ...th, textAlign: 'right' }}>Low</th>
                  <th style={{ ...th, textAlign: 'right' }}>Close</th>
                  <th style={{ ...th, textAlign: 'right' }}>Volume</th>
                  <th style={{ ...th, textAlign: 'right' }}>Updated</th>
                </tr>
              </thead>
              <tbody>
                {quotes.map((q, i) => {
                  const chg = q.last_price - q.c;
                  const chgPct = q.c ? (chg / q.c) * 100 : 0;
                  const up = chg >= 0;
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}>
                      <td style={{ ...td, fontWeight: 700, color: '#0f172a', fontFamily: 'monospace' }}>{q.symbol}</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: up ? '#16a34a' : '#dc2626' }}>
                        ₹{fmt(q.last_price)}
                        <span style={{ display: 'block', fontSize: '0.72rem', fontWeight: 400 }}>
                          {up ? '+' : ''}{fmt(chgPct, 2)}%
                        </span>
                      </td>
                      <td style={{ ...td, textAlign: 'right', color: '#475569' }}>{fmt(q.o)}</td>
                      <td style={{ ...td, textAlign: 'right', color: '#16a34a' }}>{fmt(q.h)}</td>
                      <td style={{ ...td, textAlign: 'right', color: '#dc2626' }}>{fmt(q.l)}</td>
                      <td style={{ ...td, textAlign: 'right', color: '#475569' }}>{fmt(q.c)}</td>
                      <td style={{ ...td, textAlign: 'right', color: '#64748b' }}>{Number(q.volume || 0).toLocaleString('en-IN')}</td>
                      <td style={{ ...td, textAlign: 'right', color: '#94a3b8', fontSize: '0.75rem' }}>
                        {q.updated_at ? new Date(q.updated_at).toLocaleTimeString() : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const th = { padding: '0.55rem 0.75rem', fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'left' };
const td = { padding: '0.45rem 0.75rem' };
